import { useId, useState } from 'react';
import { motion } from 'motion/react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { GuidedProcedure } from '@/stores/guidedEntryStore';
import { EmbroideryRunIcon, PrintRunIcon } from './RunIcons';

/** One spring for everything that morphs between the hero and the bar. */
export const CHOICE_TRANSITION = {
  type: 'spring',
  stiffness: 170,
  damping: 26,
  mass: 1,
} as const;

/**
 * Print or embroidery — the two runs of the guided entry (source design).
 *
 * Large while nothing is chosen: two tiles and a question, the whole step.
 * Compact once a run is chosen: the same tiles as a bar above the cards, so
 * switching stays one click away. The tiles are the same elements in both
 * states; `layout` lets them travel and resize instead of jump. Switching
 * empties both cards: a print motif is not an embroidery artwork.
 */
export function GuidedProcedureChoice({
  value,
  large,
  collapsible,
  onChange,
  embroideryHint,
}: {
  value: GuidedProcedure | null;
  large: boolean;
  /** Fold into a one-line summary that opens like an accordion. */
  collapsible: boolean;
  onChange: (procedure: GuidedProcedure) => void;
  embroideryHint: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const groupId = useId();
  const options: {
    id: GuidedProcedure;
    label: string;
    hint: string;
    Icon: typeof PrintRunIcon;
  }[] = [
    {
      id: 'print',
      label: t('creator.guided.print'),
      hint: t('creator.guided.printHint'),
      Icon: PrintRunIcon,
    },
    {
      id: 'embroidery',
      label: t('creator.guided.embroidery'),
      hint: embroideryHint,
      Icon: EmbroideryRunIcon,
    },
  ];
  const current = options.find((option) => option.id === value);
  const folded = collapsible && !open;

  return (
    <motion.section
      layout
      transition={CHOICE_TRANSITION}
      aria-label={t('creator.guided.procedure')}
      className={cn(
        'dc-panel flex flex-col rounded-card bg-[var(--paper-2)]',
        large ? 'mx-auto w-full max-w-[860px] gap-6 p-8' : 'gap-3 p-5'
      )}
    >
      {collapsible ? (
        <button
          type="button"
          aria-expanded={open}
          aria-controls={groupId}
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          className="flex w-full min-w-0 cursor-pointer items-center gap-3 text-left"
        >
          <span className="dc-type-section flex-none text-[var(--ink)]">
            {t('creator.guided.procedure')}
          </span>
          {folded && current ? (
            <span className="flex min-w-0 items-center gap-2">
              <current.Icon className="h-4 w-4 flex-none text-[var(--cobalt-500)]" />
              <span className="flex-none font-semibold text-[var(--ink)]">
                {current.label}
              </span>
              <span className="truncate text-xs text-[var(--mute)]">
                {current.hint}
              </span>
            </span>
          ) : (
            <span className="truncate text-xs text-[var(--mute)]">
              {t('creator.guided.procedureHint')}
            </span>
          )}
          <span className="ml-auto flex flex-none items-center gap-1 text-[12.5px] font-semibold text-[var(--cobalt-500)]">
            {folded
              ? t('creator.guided.changeProcedure')
              : t('creator.guided.collapseProcedure')}
            <ChevronDown
              aria-hidden="true"
              className={cn(
                'h-4 w-4 transition-transform',
                open && 'rotate-180'
              )}
            />
          </span>
        </button>
      ) : (
        <motion.div
          layout="position"
          transition={CHOICE_TRANSITION}
          className={cn(
            'flex gap-2.5',
            large ? 'flex-col items-center text-center' : 'items-baseline'
          )}
        >
          {large ? (
            <h2 className="dc-type-headline text-[var(--ink)]">
              {t('creator.guided.whichStart')}
            </h2>
          ) : (
            <span className="dc-type-section text-[var(--ink)]">
              {t('creator.guided.procedure')}
            </span>
          )}
          <span
            className={cn(
              'text-[var(--mute)]',
              large ? 'text-[14px]' : 'text-xs'
            )}
          >
            {t('creator.guided.procedureHint')}
          </span>
        </motion.div>
      )}
      {folded ? null : (
        <motion.div
          id={groupId}
          layout
          transition={CHOICE_TRANSITION}
          role="radiogroup"
          aria-label={t('creator.guided.chooseProcedure')}
          className={cn(
            'grid',
            large
              ? 'gap-4 [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]'
              : 'gap-3 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]'
          )}
        >
          {options.map(({ id, label, hint, Icon }) => {
            const selected = value === id;
            return (
              <motion.button
                key={id}
                layout
                transition={CHOICE_TRANSITION}
                whileHover={large ? { y: -2 } : undefined}
                whileTap={{ scale: 0.985 }}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => {
                  onChange(id);
                  setOpen(false);
                }}
                className={cn(
                  'cursor-pointer rounded-card text-left',
                  selected
                    ? 'bg-[var(--paper)] shadow-[inset_0_0_0_2px_var(--cobalt-500)]'
                    : 'bg-[var(--paper)] shadow-[inset_0_0_0_1px_var(--line)] hover:bg-[var(--cobalt-100)]'
                )}
              >
                <span
                  className={cn(
                    'flex',
                    large
                      ? 'min-h-[220px] flex-col items-center justify-center gap-4 px-6 py-8 text-center'
                      : 'items-center gap-3.5 px-4 py-3.5'
                  )}
                >
                  <motion.span
                    layout
                    transition={CHOICE_TRANSITION}
                    className={cn(
                      'grid flex-none place-items-center',
                      large
                        ? 'h-16 w-16 rounded-card'
                        : 'h-10 w-10 rounded-control',
                      selected
                        ? 'bg-[var(--cobalt-100)] text-[var(--cobalt-500)]'
                        : 'bg-[var(--paper-2)] text-[var(--ink-2)]'
                    )}
                  >
                    <Icon className={large ? 'h-8 w-8' : 'h-5 w-5'} />
                  </motion.span>
                  <motion.span
                    layout="position"
                    transition={CHOICE_TRANSITION}
                    className={cn(
                      'flex min-w-0 flex-col gap-0.5',
                      large && 'items-center gap-1.5'
                    )}
                  >
                    <span
                      className="dc-type-card-title text-[var(--ink)]"
                      data-size={large ? 'lg' : undefined}
                    >
                      {label}
                    </span>
                    <span
                      className={cn(
                        'leading-[1.4] text-[var(--ink-2)]',
                        large ? 'text-[13.5px]' : 'text-[12.5px]'
                      )}
                    >
                      {hint}
                    </span>
                  </motion.span>
                  {!large && (
                    <span
                      aria-hidden="true"
                      className={cn(
                        'ml-auto h-4 w-4 flex-none rounded-full',
                        selected
                          ? 'border-[5px] border-[var(--cobalt-500)] bg-paper'
                          : 'border border-[var(--line)] bg-paper'
                      )}
                    />
                  )}
                </span>
              </motion.button>
            );
          })}
        </motion.div>
      )}
    </motion.section>
  );
}
